import React, { useState, useMemo } from 'react';
import { ShieldCheck, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, WARNING, ERROR } from '@/styles/tokens';
import type { CanvasAction, AgentVisualResponse } from '@/services/api/agentApi';
import type { SnapshotDiff } from '@/services/editor/CanvasActionApplicator';

interface AgentFeedbackPanelProps {
  response: AgentVisualResponse;
  onApply: () => void;
  onDiscard: () => void;
  onModify: () => void;
  changeSummary?: SnapshotDiff;
}

/**
 * Confirmation panel shown when agent actions require user approval
 * (replace_page, >15 actions, delete_component, low confidence).
 */
export const AgentFeedbackPanel: React.FC<AgentFeedbackPanelProps> = ({
  response,
  onApply,
  onDiscard,
  onModify,
  changeSummary,
}) => {
  const [showReasoning, setShowReasoning] = useState(false);

  const actions = response.actions || [];
  const summary = useMemo(() => summarizeActions(actions), [actions]);
  const avgConfidence = useMemo(() => {
    const confidences = actions
      .map(a => a.metadata?.confidence)
      .filter((c): c is number => typeof c === 'number');
    if (confidences.length === 0) return null;
    return confidences.reduce((sum, c) => sum + c, 0) / confidences.length;
  }, [actions]);

  const reasoning = actions
    .map(a => a.metadata?.reasoning)
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div
      style={{
        border: `1px solid ${WARNING.DEFAULT}40`,
        borderRadius: 8,
        padding: 12,
        backgroundColor: `${WARNING.DEFAULT}08`,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ShieldCheck className="w-4 h-4" style={{ color: WARNING.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: MID_GRAY.DEFAULT }}>
          Review before applying
        </span>
      </div>

      {/* Action summary */}
      <div style={{ fontSize: 12, color: MID_GRAY.DEFAULT, marginBottom: 8, lineHeight: 1.5 }}>
        {summary.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>

      {/* Confidence indicator */}
      {avgConfidence !== null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 8,
          fontSize: 12,
        }}>
          {avgConfidence < 0.5 ? (
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: ERROR.DEFAULT }} />
          ) : (
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: avgConfidence >= 0.8 ? FOREST_GREEN.light
                : avgConfidence >= 0.5 ? WARNING.DEFAULT
                : ERROR.DEFAULT,
            }} />
          )}
          <span style={{
            color: avgConfidence >= 0.8 ? FOREST_GREEN.light
              : avgConfidence >= 0.5 ? WARNING.DEFAULT
              : ERROR.DEFAULT,
          }}>
            Confidence: {Math.round(avgConfidence * 100)}%
          </span>
        </div>
      )}

      {/* Collapsible reasoning */}
      {reasoning.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <button
            onClick={() => setShowReasoning(!showReasoning)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 12,
              color: MID_GRAY.DEFAULT,
            }}
          >
            {showReasoning ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Agent reasoning
          </button>
          {showReasoning && (
            <div style={{
              marginTop: 4,
              padding: '6px 8px',
              borderRadius: 4,
              backgroundColor: `${MID_GRAY.DEFAULT}08`,
              fontSize: 11,
              color: MID_GRAY.DEFAULT,
              lineHeight: 1.5,
            }}>
              {reasoning.map((r, i) => (
                <div key={i} style={{ marginBottom: i < reasoning.length - 1 ? 4 : 0 }}>
                  {r}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Change summary (visual diff) */}
      {changeSummary && (changeSummary.added.length > 0 || changeSummary.modified.length > 0 || changeSummary.removed.length > 0) && (
        <div style={{ padding: '8px 0', borderTop: `1px solid ${MID_GRAY.DEFAULT}15`, fontSize: 12, color: MID_GRAY.DEFAULT, marginBottom: 8 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{changeSummary.summary}</div>
          {changeSummary.modified.slice(0, 3).map(mod => (
            <div key={mod.componentId} style={{ marginLeft: 8, lineHeight: 1.5 }}>
              {'\u2022'} {mod.componentName || mod.componentType}: {mod.changes.join(', ')}
            </div>
          ))}
          {changeSummary.modified.length > 3 && (
            <div style={{ marginLeft: 8, fontStyle: 'italic' }}>
              +{changeSummary.modified.length - 3} more...
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onApply}
          style={{
            flex: 1,
            padding: '6px 12px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: FOREST_GREEN.DEFAULT,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Apply Changes
        </button>
        <button
          onClick={onDiscard}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: `1px solid ${MID_GRAY.DEFAULT}30`,
            backgroundColor: 'transparent',
            color: MID_GRAY.DEFAULT,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Discard
        </button>
        <button
          onClick={onModify}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: `1px solid ${FOREST_GREEN.DEFAULT}30`,
            backgroundColor: 'transparent',
            color: FOREST_GREEN.DEFAULT,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Modify First...
        </button>
      </div>
    </div>
  );
};

function summarizeActions(actions: CanvasAction[]): string[] {
  const counts: Record<string, number> = {};
  for (const a of actions) {
    counts[a.type] = (counts[a.type] || 0) + 1;
  }

  const lines: string[] = [];
  const labels: Record<string, string> = {
    add_component: 'Add',
    update_component: 'Update',
    delete_component: 'Delete',
    reorder_children: 'Reorder',
    batch_update: 'Batch update',
    replace_page: 'Replace page',
    add_page: 'Add page',
    update_page: 'Update page',
  };

  for (const [type, count] of Object.entries(counts)) {
    const label = labels[type] || type;
    lines.push(`${label} ${count} component${count > 1 ? 's' : ''}`);
  }

  lines.push(`Total: ${actions.length} action${actions.length !== 1 ? 's' : ''}`);
  return lines;
}
